import React from 'react';
import type { HTMLElementEvent } from '../../../models/Types';

import type { Text } from '@zillow/constellation';
import { AdornedInput, Adornment, FormField, Input, Label, Paragraph } from '@zillow/constellation';

import type { Section } from '../../..//models';
import { TENANT_DEFAULT } from '../../../constants';
import { stopPropagationIfInvalidNumber } from '../../../utils/validatorTypes/PositiveIntegerValidator';
import InlineTooltip from '../../../components/Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function TenantDefault_v4({ section, setSectionValues }: Props) {
  function onDaysAfterUnpaidChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_UNPAID]: value,
    });
  }

  function onDaysAfterReceivingNoticeChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_RECEIVING_NOTICE]: value,
    });
  }

  return (
    <>
      <Paragraph fontType="h6" marginBottom="xs">
        How long is the grace period before the tenant{' '}
        <InlineTooltip tooltipBodyText="A default occurs when, after receiving written notice and an opportunity to cure any breach of the lease, a tenant fails to cure the breach within the grace period provided in the lease.">
          defaults
        </InlineTooltip>{' '}
        on the lease?
      </Paragraph>
      <FormField
        marginBottom="sm"
        label={
          <Label>
            Number of days after receiving written notice from landlord of failure to pay monthly or additional rent
          </Label>
        }
        control={
          <AdornedInput
            input={
              <Input
                type="number"
                onChange={onDaysAfterUnpaidChange}
                onKeyDown={stopPropagationIfInvalidNumber}
                value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_UNPAID)}
              />
            }
            rightAdornment={<Adornment>days</Adornment>}
          />
        }
      />
      <FormField
        marginBottom="sm"
        label={
          <Label>
            Number of days after receiving written notice from landlord of failure to live up to other obligations
          </Label>
        }
        control={
          <AdornedInput
            input={
              <Input
                type="number"
                onChange={onDaysAfterReceivingNoticeChange}
                onKeyDown={stopPropagationIfInvalidNumber}
                value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_RECEIVING_NOTICE)}
              />
            }
            rightAdornment={<Adornment>days</Adornment>}
          />
        }
      />
    </>
  );
}

TenantDefault_v4.description = 'Asks between days since failure and since receiving notice';

export default TenantDefault_v4;
